// The desktop's settings behind one button in its bar: the clipboard between this computer and the desktop, the
// stream's resolution and what it gives up on a slow link. A menu-density popover, like the Organize menu.

import { IconCheck, IconClipboard, IconClipboardCheck, IconClipboardX, IconSettings } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useStore } from '../store';
import { Button, Ic, Pix, Tool, presence, usePresence } from './ds';
import { modLabel } from './keys';
import { ModKey } from './ModKey';
import { POLICIES, setStreamPolicy } from './StreamMode';

type Clip = { state: PermissionState | 'unsupported'; ask: () => void };
type Choice = { value: string; label: string };

function Choices({ heading, items, value, onPick, testid }: { heading: string; items: Choice[]; value?: string; onPick: (v: string) => void; testid: string }) {
  return (
    <div role="group" aria-label={heading}>
      <div className="ws-menu-label">
        <Pix>{heading}</Pix>
      </div>
      {items.map((i) => (
        <button
          key={i.value}
          type="button"
          className="ws-menu-item"
          role="menuitemradio"
          aria-checked={i.value === value}
          data-testid={`${testid}-${i.value}`}
          onClick={() => i.value !== value && onPick(i.value)}
        >
          <span className="ws-menu-check">{i.value === value && <Ic icon={IconCheck} size={14} stroke={2} />}</span>
          {i.label}
        </button>
      ))}
    </div>
  );
}

export function DesktopSettings({ clip, res, resItems, onRes }: { clip: Clip; res: string; resItems: Choice[]; onRes: (v: string) => void }) {
  const net = useStore((s) => s.desktopNet);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const p = usePresence(open, 120);
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  const keys = `${modLabel()}C / ${modLabel()}V`;
  const clipRow =
    clip.state === 'granted'
      ? { icon: IconClipboardCheck, text: 'On', tone: 'live' }
      : clip.state === 'denied'
        ? { icon: IconClipboardX, text: 'Blocked', tone: undefined }
        : clip.state === 'prompt'
          ? { icon: IconClipboard, text: 'Off', tone: undefined }
          : null;
  return (
    <span className="ws-menu-wrap" ref={ref}>
      <Tool icon={IconSettings} radius={8} label="Desktop settings" sub="Clipboard, resolution, network" pressed={open} aria-expanded={open} aria-haspopup="dialog" data-testid="desktop-settings" onClick={() => setOpen((o) => !o)} />
      {p.mounted && (
        <div className="ws-menu ws-dset ds-float" role="dialog" aria-label="Desktop settings" {...presence(p)}>
          {clipRow && (
            <>
              <div className="ws-menu-label">
                <Pix>Clipboard</Pix>
              </div>
              <div className="ws-dset-clip" title={clip.state === 'denied' ? 'Clipboard is blocked for this site: allow it in the browser’s site settings' : `${keys} work between this computer and the desktop`} data-tone={clipRow.tone} data-testid={`clipboard-${clip.state === 'granted' ? 'on' : clip.state === 'denied' ? 'blocked' : 'off'}`}>
                <span className="ws-menu-icon">
                  <Ic icon={clipRow.icon} size={16} />
                </span>
                <span className="ws-dset-clip-text">
                  <span className="ws-dset-clip-state">{clipRow.text}</span>
                  <span className="ws-dset-clip-keys">
                    (<ModKey k="C" /> / <ModKey k="V" />)
                  </span>
                </span>
                {clip.state === 'prompt' && (
                  <Button size="xs" radius={6} onClick={clip.ask} data-testid="clipboard-allow">
                    Allow
                  </Button>
                )}
              </div>
              <div className="ws-menu-rule" role="separator" />
            </>
          )}
          <Choices heading="Resolution" items={resItems} value={res} onPick={onRes} testid="desktop-res" />
          {net && (
            <>
              <div className="ws-menu-rule" role="separator" />
              <Choices heading="Network" items={POLICIES} value={net.policy} onPick={(v) => setStreamPolicy(v as (typeof POLICIES)[number]['value'])} testid="desktop-netpolicy" />
            </>
          )}
        </div>
      )}
    </span>
  );
}
